import React, { useState, useEffect } from "react";
import axiosClient from "../../config/axiosClient";
import API_ENDPOINTS from "../../config/api";
import SupplierModal from "./SupplierModal"; 
import "./SupplierList.css"; 

const PAGE_SIZE = 10;

const SupplierList = () => {
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState("");
  const [selectedOrigin, setSelectedOrigin] = useState("All");
  const [showModal, setShowModal] = useState(false);
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    fetchSuppliers();
  }, []);

  const fetchSuppliers = async () => {
    try {
      const res = await axiosClient.get(API_ENDPOINTS.suppliers);
      setSuppliers(res.data || []);
    } catch (error) {
      console.error("❌ Error loading Suppliers:", error);
    }
  };

  const uniqueOrigins = ["All", ...new Set(suppliers.map(s => s.origin || "Other"))];

  const handleExport = async () => {
    try {
      const response = await axiosClient.get(`${API_ENDPOINTS.suppliers}/export`, {
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      const dateStr = new Date().toISOString().split('T')[0];
      link.setAttribute("download", `Suppliers_${dateStr}.xlsx`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      alert("Export failed!");
    }
  };

  const handleImport = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const formData = new FormData();
    formData.append("file", file);
    try {
      const response = await axiosClient.post(`${API_ENDPOINTS.suppliers}/import`, formData, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      if (response.data.success) {
        alert("Import successful!");
        fetchSuppliers();
      }
    } catch (error) {
      alert(error.response?.data?.message || "Import failed!");
    } finally {
      e.target.value = null; 
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm(`Delete supplier ${id}?`)) return;
    try {
      await axiosClient.delete(`${API_ENDPOINTS.suppliers}/${id}`);
      fetchSuppliers();
    } catch (error) {
      console.error("Delete error:", error);
    }
  };

  const handleSave = async (formData) => {
    try {
      const config = { headers: { "Content-Type": "multipart/form-data" } };
      if (editingSupplier) {
        await axiosClient.put(`${API_ENDPOINTS.suppliers}/${editingSupplier.id}`, formData, config);
      } else {
        await axiosClient.post(API_ENDPOINTS.suppliers, formData, config);
      }
      fetchSuppliers();
      setShowModal(false);
      setEditingSupplier(null);
    } catch (error) {
      alert(error.response?.data?.message || "Error saving data!");
    }
  };

  const filteredData = suppliers.filter((s) => {
    const searchTerm = search.toLowerCase();
    const matchesSearch = (
      (s.id || "").toLowerCase().includes(searchTerm) ||
      (s.name || "").toLowerCase().includes(searchTerm) ||
      (s.email || "").toLowerCase().includes(searchTerm) ||
      (s.fieldOfActivity || "").toLowerCase().includes(searchTerm)
    );
    const matchesOrigin = selectedOrigin === "All" || (s.origin || "Other") === selectedOrigin;
    return matchesSearch && matchesOrigin;
  });

  const totalPages = Math.ceil(filteredData.length / PAGE_SIZE);
  const currentItems = filteredData.slice((currentPage - 1) * PAGE_SIZE, currentPage * PAGE_SIZE);

  const groupedItems = currentItems.reduce((groups, item) => {
    const origin = item.origin || "Other";
    if (!groups[origin]) groups[origin] = [];
    groups[origin].push(item);
    return groups;
  }, {});

  const currentOriginsOnPage = Object.keys(groupedItems).sort();

  const thStyle = { 
    backgroundColor: "#f8f9fc", 
    color: "#1cc88a", // Màu xanh lá cho Supplier
    fontWeight: "bold", 
    fontSize: "0.75rem", 
    textTransform: "uppercase" 
  };

  return (
    <div className="container-fluid py-4">
      <div className="card shadow-sm border-0">
        <div className="card-header bg-white py-3 d-flex justify-content-between align-items-center flex-wrap gap-3">
          <h5 className="m-0 font-weight-bold text-dark text-uppercase">Supplier Directory</h5>
          
          <div className="d-flex gap-2 align-items-center flex-wrap">
            <select 
              className="form-select form-select-sm"
              style={{ width: "150px", borderRadius: "20px" }}
              value={selectedOrigin}
              onChange={(e) => { setSelectedOrigin(e.target.value); setCurrentPage(1); }}
            >
              {uniqueOrigins.map(origin => (
                <option key={origin} value={origin}>{origin === "All" ? "All Regions" : origin}</option>
              ))}
            </select>

            <input
              type="text"
              className="form-control form-control-sm ps-4"
              style={{ width: "220px", borderRadius: "20px" }}
              placeholder="Search name, activity..."
              onChange={(e) => { setSearch(e.target.value); setCurrentPage(1); }}
            />

            <div className="btn-group shadow-sm">
              <button className="btn btn-sm btn-light border" onClick={handleExport}>
                <i className="fas fa-file-export text-primary"></i>
              </button>
              <label className="btn btn-sm btn-light border mb-0" style={{ cursor: "pointer" }}>
                <i className="fas fa-file-import text-warning"></i>
                <input type="file" accept=".xlsx, .xls, .csv" hidden onChange={handleImport} />
              </label>
            </div>

            <button
              className="btn btn-sm btn-success px-3 rounded-pill fw-bold"
              onClick={() => { setEditingSupplier(null); setShowModal(true); }}
            >
              <i className="fas fa-plus me-1"></i> Add Supplier
            </button>
          </div>
        </div>

        <div className="card-body p-0">
          <div className="table-responsive">
            <table className="table table-hover align-middle mb-0">
              <thead>
                <tr>
                  <th className="text-center" style={thStyle}>ID</th>
                  <th style={thStyle}>Company Details</th>
                  <th style={thStyle}>Contact Info</th>
                  <th style={thStyle}>Field of Activity</th>
                  <th className="text-center" style={thStyle}>Actions</th>
                </tr>
              </thead>
              <tbody>
                {currentItems.length > 0 ? (
                  currentOriginsOnPage.map((origin) => (
                    <React.Fragment key={origin}>
                      <tr className="bg-light">
                        <td colSpan="5" className="py-1 ps-3 border-start border-success border-4">
                          <span className="badge bg-success px-2 rounded-pill text-uppercase" style={{fontSize: '0.65rem'}}>
                            {origin}
                          </span>
                        </td>
                      </tr>
                      {groupedItems[origin].map((s) => (
                        <tr key={s.id}>
                          <td className="text-center">
                            <span className="badge bg-light text-success border px-2 py-1">{s.id}</span>
                          </td>
                          <td>
                            <div className="fw-bold text-dark">{s.name}</div>
                            <div className="small text-muted text-truncate" style={{ maxWidth: "300px" }}>
                              {s.address || "No address"}
                            </div>
                          </td>
                          <td>
                            <div className="small"><i className="fas fa-phone me-1 text-muted"></i>{s.phone}</div>
                            <div className="small text-primary"><i className="fas fa-envelope me-1 text-muted"></i>{s.email}</div>
                          </td>
                          <td className="small">{s.fieldOfActivity}</td>
                          <td className="text-center">
                            <div className="d-flex justify-content-center gap-2">
                              <button className="btn btn-sm btn-outline-success border-0" onClick={() => { setEditingSupplier(s); setShowModal(true); }}>
                                <i className="fas fa-edit"></i>
                              </button>
                              <button className="btn btn-sm btn-outline-danger border-0" onClick={() => handleDelete(s.id)}>
                                <i className="fas fa-trash-alt"></i>
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </React.Fragment>
                  ))
                ) : (
                  <tr>
                    <td colSpan="5" className="text-center py-5 text-muted">No suppliers found.</td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          <div className="d-flex justify-content-between align-items-center p-3 bg-light border-top">
            <nav>
              <ul className="pagination pagination-sm mb-0">
                <li className={`page-item ${currentPage === 1 ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage - 1)}><i className="fas fa-chevron-left"></i></button>
                </li>
                {[...Array(totalPages)].map((_, i) => (
                  <li key={i} className={`page-item ${currentPage === i + 1 ? "active" : ""}`}>
                    <button className="page-link" onClick={() => setCurrentPage(i + 1)}>{i + 1}</button>
                  </li>
                ))}
                <li className={`page-item ${currentPage === totalPages ? "disabled" : ""}`}>
                  <button className="page-link" onClick={() => setCurrentPage(currentPage + 1)}><i className="fas fa-chevron-right"></i></button>
                </li>
              </ul>
            </nav>
            <span className="small text-muted fw-bold">Total: {filteredData.length} Suppliers</span>
          </div>
        </div>
      </div>

      {showModal && (
        <SupplierModal
          show={showModal}
          initialData={editingSupplier}
          onClose={() => { setShowModal(false); setEditingSupplier(null); }}
          onSave={handleSave}
        />
      )}
    </div>
  );
};

export default SupplierList;